import { useEffect, useState } from "react";
import {
  LaterItem,
  ScheduledSend,
  formatWhen,
  loadLaterItems,
  loadScheduledSends,
  removeLaterItem,
  removeScheduledSend,
  subscribeLater,
} from "./later";
import { Selection } from "./useChatData";

/**
 * The "Later" rail tab — saved messages, pending reminders, and
 * scheduled sends, each jumping back to its conversation on click.
 */
export default function LaterPanel({
  onClose,
  onOpen,
}: {
  onClose: () => void;
  onOpen: (s: Selection) => void;
}) {
  const [items, setItems] = useState<LaterItem[]>(loadLaterItems);
  const [sends, setSends] = useState<ScheduledSend[]>(loadScheduledSends);

  useEffect(
    () =>
      subscribeLater(() => {
        setItems(loadLaterItems());
        setSends(loadScheduledSends());
      }),
    []
  );

  const open = (conversation: LaterItem["conversation"], focusMessageId?: number) => {
    onOpen({
      kind: conversation.kind,
      id: conversation.id,
      name: conversation.name,
      subtitle: conversation.kind === "group" ? "Channel" : "Direct message",
      focusMessageId,
    });
    onClose();
  };

  const saved = items.filter((i) => i.kind === "saved");
  const reminders = items.filter((i) => i.kind === "reminder");

  return (
    <aside className="details-panel later-panel">
      <div className="modal-head">
        <b>Later</b>
        <button className="hbtn" onClick={onClose} aria-label="Close">✕</button>
      </div>

      <div className="modal-section">
        <div className="modal-label">Saved</div>
        <div className="modal-list">
          {saved.length === 0 && <div className="sect-empty">Nothing saved yet — hover a message and hit 🔖</div>}
          {saved.map((i) => (
            <div className="later-item" key={i.id}>
              <button className="later-item__main" onClick={() => open(i.conversation, i.messageId)}>
                <span className="later-item__text">{i.text}</span>
                <span className="later-item__meta">
                  {i.from} · {i.conversation.kind === "group" ? `#${i.conversation.name}` : i.conversation.name}
                </span>
              </button>
              <button className="later-item__remove" title="Remove" onClick={() => removeLaterItem(i.id)}>✕</button>
            </div>
          ))}
        </div>
      </div>

      <div className="modal-section">
        <div className="modal-label">Reminders</div>
        <div className="modal-list">
          {reminders.length === 0 && <div className="sect-empty">No reminders</div>}
          {reminders.map((i) => (
            <div className="later-item" key={i.id}>
              <button className="later-item__main" onClick={() => open(i.conversation, i.messageId)}>
                <span className="later-item__text">{i.text}</span>
                <span className="later-item__meta">
                  {i.notified ? "fired" : `due ${formatWhen(i.remindAt ?? 0)}`} · {i.from}
                </span>
              </button>
              <button className="later-item__remove" title="Remove" onClick={() => removeLaterItem(i.id)}>✕</button>
            </div>
          ))}
        </div>
      </div>

      <div className="modal-section">
        <div className="modal-label">Scheduled sends</div>
        <div className="modal-list">
          {sends.length === 0 && <div className="sect-empty">Nothing scheduled</div>}
          {sends.map((s) => (
            <div className="later-item" key={s.id}>
              <button
                className="later-item__main"
                onClick={() =>
                  open({ kind: s.target.kind, id: s.target.id, name: s.target.name })
                }
              >
                <span className="later-item__text">{s.text}</span>
                <span className="later-item__meta">
                  sends {formatWhen(s.sendAt)} → {s.target.kind === "group" ? `#${s.target.name}` : s.target.name}
                </span>
              </button>
              <button className="later-item__remove" title="Cancel" onClick={() => removeScheduledSend(s.id)}>✕</button>
            </div>
          ))}
        </div>
      </div>

      <div className="later-note">
        Reminders and scheduled sends fire while dbm. chat is open — anything past due
        fires the next time it loads.
      </div>
    </aside>
  );
}
